import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  VideoCard,
  getLikedSet,
  saveLikedSet,
} from "../../components/ShotsSection";
import { getShots, Shot } from "../../services/shotsApi";
import { isPrerendering } from "../../utils/prerender";
import { V2, pickLang } from "../copy";
import { Arrow, Display, Eyebrow } from "./Primitives";
import { route } from "../paths";

/** Horizontal video reel — reuses the production VideoCard on an ink stage. */
const ReelV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].reel;

  const [shots, setShots] = useState<Shot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [likedIds, setLikedIds] = useState<Set<string>>(getLikedSet);

  useEffect(() => {
    if (isPrerendering()) return; // snapshot keeps the skeleton
    let alive = true;
    getShots(1, 8)
      .then((d) => {
        if (!alive) return;
        setShots(d.shots);
        if (d.shots[0]) setActiveId(d.shots[0].id);
      })
      .catch(() => alive && setShots([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onLike = useCallback((id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveLikedSet(next);
      return next;
    });
  }, []);
  const onActivate = useCallback((id: string) => setActiveId(id), []);
  const onDeactivate = useCallback(
    (id: string) => setActiveId((cur) => (cur === id ? null : cur)),
    [],
  );

  if (!loading && shots.length === 0) return null;

  return (
    <section id="v2-reel" className="bg-ink py-24 text-bone sm:py-32">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow index="02">{c.label}</Eyebrow>
            <Display className="mt-5 text-[clamp(2.6rem,7vw,6.5rem)]">
              {c.title}
            </Display>
          </div>
          <div className="v2-reveal max-w-sm" data-delay="2">
            <p className="text-bone/60">{c.subtitle}</p>
            <Link
              to={route(lang, "shots")}
              className="v2-link mt-4 inline-flex items-center gap-2 text-sm"
            >
              {c.more}
              <Arrow />
            </Link>
          </div>
        </div>
      </div>

      <div
        className="v2-noscroll mt-12 flex snap-x gap-5 overflow-x-auto pb-6 pr-6 pl-[max(1rem,calc((100vw-1400px)/2+1.5rem))]"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="w-[260px] flex-shrink-0 animate-pulse rounded-3xl bg-bone/5 sm:w-[300px]"
                style={{ aspectRatio: "9/16" }}
              />
            ))
          : shots.map((s) => (
              <div key={s.id} className="snap-center">
                <VideoCard
                  shot={s}
                  globalMuted={true}
                  liked={likedIds.has(s.id)}
                  onLike={onLike}
                  activeId={activeId}
                  onActivate={onActivate}
                  onDeactivate={onDeactivate}
                />
              </div>
            ))}
      </div>
    </section>
  );
};

export default ReelV2;
